import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LineChart, LogIn, LogOut, ChevronDown, Users, Settings, ShieldCheck, Mail, CreditCard, Lightbulb } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import NotificationsBell from "./NotificationsBell";
import { AccountSheet, Avatar, MobileTabBar } from "./MobileNav";

import { tierStyle } from "../lib/tiers";

const TIER_LABELS = { free: "Gratuit", essentiel: "Essentiel", pro: "Pro", max: "Max" };

// Pastille de l'offre du membre, aux couleurs du site (Gratuit gris, Essentiel orange, Pro vert, Max violet)
function TierBadge({ tier }) {
  return (
    <span
      data-testid="nav-tier-badge"
      className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] border ${tierStyle(tier || "free").badge}`}
    >
      {TIER_LABELS[tier] || "Gratuit"}
    </span>
  );
}

const baseLinks = [
  { to: "/", label: "Accueil" },
  { to: "/articles", label: "Analyses" },
  { to: "/portefeuilles-sideb", label: "Portefeuilles Side B" },
  { to: "/cours", label: "Cours" },
  { to: "/simulateurs", label: "Outils" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const isAdmin = user && user.role === "admin";
  const tier = user?.subscription_tier || "free";
  const isPaid = tier !== "free";
  const links = baseLinks;
  // Nombre d'idées non traitées (statut "new") — requête légère, rafraîchie toutes les 60 s
  const { data: ideasCount } = useQuery({
    queryKey: ["admin-ideas-count-new"],
    queryFn: async () => (await api.get("/admin/ideas/count-new")).data,
    enabled: !!isAdmin,
    refetchInterval: 60000,
  });
  const newIdeas = ideasCount?.count || 0;

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <>
    <header
      className="sticky top-0 z-50 glass border-b border-white/5"
      data-testid="app-navbar"
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 h-14 md:h-16 flex items-center justify-between gap-4 md:gap-6">
        <Link
          to="/"
          className="flex items-center gap-4 group shrink-0"
          data-testid="nav-logo-link"
        >
          <img
            src="/logo-sideb.png"
            alt="Side B"
            className="h-8 md:h-10 w-auto object-contain"
            data-testid="nav-logo-img"
          />
          <span
            className="hidden xl:inline-block italic font-bold text-sm text-zinc-300 tracking-wide whitespace-nowrap select-none pr-1"
            style={{ fontFamily: "'Work Sans', sans-serif" }}
            data-testid="nav-tagline"
          >
            Financial Research
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-emerald-400 bg-emerald-500/10"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative px-4 py-2 text-sm rounded-md text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1" data-testid="nav-admin-menu">
                  Admin <ChevronDown className="w-3.5 h-3.5" />
                  {newIdeas > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-400" data-testid="nav-admin-dot" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-white/10 min-w-[220px]">
                <DropdownMenuLabel className="text-xs uppercase tracking-[0.15em] text-zinc-500">Console admin</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="nav-admin-articles">
                  <Link to="/admin/articles" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" /> Analyses
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-courses">
                  <Link to="/admin/cours" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" /> Cours
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-sideb">
                  <Link to="/admin/portefeuilles-sideb" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" /> Portefeuilles Side B
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-reviews">
                  <Link to="/admin/avis" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" /> Avis à modérer
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-ideas">
                  <Link to="/admin/idees" className="cursor-pointer flex items-center w-full">
                    <Lightbulb className="w-4 h-4 mr-2" /> Idées
                    {newIdeas > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-semibold tabular-nums" data-testid="nav-admin-ideas-badge">
                        {newIdeas}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-homecards">
                  <Link to="/admin/home-cards" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" /> Cartes accueil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-newsletter">
                  <Link to="/admin/newsletter" className="cursor-pointer">
                    <Mail className="w-4 h-4 mr-2" /> Newsletter
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-users">
                  <Link to="/admin/utilisateurs" className="cursor-pointer">
                    <Users className="w-4 h-4 mr-2" /> Utilisateurs
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-security">
                  <Link to="/admin/securite" className="cursor-pointer">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Sécurité 2FA
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-stats">
                  <Link to="/admin/statistiques" className="cursor-pointer">
                    <LineChart className="w-4 h-4 mr-2" /> Statistiques
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <NotificationsBell />
              {!isAdmin && tier !== "max" && (
                <Button
                  asChild
                  size="sm"
                  className="bg-emerald-500 text-black hover:bg-emerald-300"
                  data-testid="nav-upgrade"
                >
                  <Link to="/upgrade">{isPaid ? "Changer d'offre" : "Upgrade"}</Link>
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 mx-1 px-2 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                    data-testid="nav-user-menu"
                  >
                    <TierBadge tier={tier} />
                    <span className="text-xs text-zinc-400 max-w-[150px] truncate" data-testid="nav-user-email">
                      {user.email}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-white/10 min-w-[200px]">
                  <DropdownMenuLabel className="text-xs text-zinc-500 truncate normal-case">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="nav-account-link">
                    <Link to="/account" className="cursor-pointer">
                      <CreditCard className="w-4 h-4 mr-2" /> Mon abonnement
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="border-white/10 bg-transparent hover:bg-white/5"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Déconnexion
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav("/login")}
                data-testid="nav-login-btn"
                className="hover:bg-white/5"
              >
                <LogIn className="w-4 h-4 mr-1.5" /> Connexion
              </Button>
              <Button
                size="sm"
                onClick={() => nav("/login")}
                data-testid="nav-register-btn"
                className="bg-emerald-500 text-black hover:bg-emerald-300"
              >
                Inscription
              </Button>
            </>
          )}
        </div>

        {/* Téléphone : notifications + bouton compte (ou Connexion) ; les sections sont dans la barre du bas */}
        <div className="md:hidden flex items-center gap-1.5">
          {user ? (
            <>
              <NotificationsBell />
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="relative p-1 rounded-full"
                aria-label="Mon compte"
                data-testid="nav-mobile-account"
              >
                <Avatar user={user} />
                {isAdmin && newIdeas > 0 && <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-[#0A0A0A]" />}
              </button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => nav("/login")}
              data-testid="nav-mobile-login-btn"
              className="h-8 bg-emerald-500 text-black hover:bg-emerald-300"
            >
              <LogIn className="w-4 h-4 mr-1.5" /> Connexion
            </Button>
          )}
        </div>
      </div>
    </header>
    {/* En dehors de l'en-tête : son flou d'arrière-plan empêcherait la barre du bas de rester fixe */}
    <AccountSheet
      open={open}
      onClose={() => setOpen(false)}
      user={user}
      isAdmin={isAdmin}
      newIdeas={newIdeas}
      onLogout={() => { setOpen(false); handleLogout(); }}
    />
    <MobileTabBar />
    </>
  );
}
